/**
 * @phxWeekTimes
 */
import * as _4 from 'lodash4';
export interface IDay {
  date: moment.Moment;
  day: string;
  day_week: number;
  time: string;
  am: boolean;
  selected: boolean;
}
export interface IWeekDay {
  date: string;
  day: string;
  short_day: string;
  show: boolean;
  day_week: number;
  month: string;
  short_month: string;
}

type t_day_part = 'AM' | 'PM';

interface IAvailableTimeSlots {
  am: IDay[];
  pm: IDay[];
  all: IDay[];
}
export class PhxWeekTimesController {
  date: string;
  week: IWeekDay[];
  data: IDay[];
  selectedDay: number;
  availableTimeSlots: IAvailableTimeSlots = {
    am: [],
    pm: [],
    all: []
  };
  previous_week_first_day: moment.Moment;
  next_week_first_day: moment.Moment;
  selected_Time: IDay;
  selected_Date: number;
  private __selectedDayPart: t_day_part;
  timings: moment.Moment[];
  onSelect: (arg: { item: IDay }) => void;
  selectedTime: IDay;
  onNextWeekSelection: () => Promise<moment.Moment[]>;
  onPreviousWeekSelection: () => Promise<moment.Moment[]>;

  $onInit(): void {
    this.data = this.constructDates(this.timings);
    const start_of_week: moment.Moment =
      _4.size(this.data) > 0 ? moment(this.data[0].date).startOf('week') : moment().startOf('week');
    this.week = this.getWeek(start_of_week);
    if (_4.isEmpty(this.selectedTime)) {
      this.autoSelectFirstAvailableDateInCurrentWeek();
    } else {
      this.autoSelectSelectedDateTime(this.selectedTime);
    }
  }
  // Definitely a Time utility Function
  // date is always the start if the week.
  getWeek(date: moment.Moment): IWeekDay[] {
    const week: IWeekDay[] = [];
    for (let i: number = 0; i < 7; i++) {
      const dayMoment = moment(date).add(i, 'days');
      week.push({
        date: dayMoment.format('DD'),
        day: dayMoment.format('dddd'),
        short_day: dayMoment.format('ddd'),
        month: dayMoment.format('MMMM'),
        short_month: dayMoment.format('MMM'),
        show: dayMoment.isoWeekday() < 6,
        day_week: i
      });
    }
    this.previous_week_first_day = moment(date).subtract(7, 'days');
    this.next_week_first_day = moment(date).add(7, 'days');
    return week;
  }

  constructDates(dates: moment.Moment[]): IDay[] {
    return _4.map(
      dates,
      (date): IDay => ({
        date,
        day: date.format('ddd'),
        day_week: date.day(),
        time: date.format('hh:mm A'),
        am: date.format('A') === 'AM',
        selected: false
      })
    );
  }

  getTimesForDay(day: number) {
    return _4.filter(this.data, { day_week: day });
  }

  availabeTimesForDay(day: number) {
    return this.getTimesForDay(day).length;
  }

  hasTimeAvailableByDay(day: number) {
    return this.availabeTimesForDay(day) > 0;
  }

  buildAvailableSlotsForDay(day: number): IAvailableTimeSlots {
    const allSlots = this.getTimesForDay(day);
    const partitionSlots = _4.partition(allSlots, { am: true });
    this.availableTimeSlots = {
      am: partitionSlots[0],
      pm: partitionSlots[1],
      all: allSlots
    };
    return this.availableTimeSlots;
  }

  selectDay(day: number) {
    this.selectedDay = day;
    this.showNextAvailableTimes(day);
  }

  autoSelectFirstAvailableDateInCurrentWeek() {
    this.selectedDay = -1;
    const firstAvailableTime = _4.first(this.data);
    if (firstAvailableTime) {
      this.selectDay(firstAvailableTime.day_week);
    }
  }

  showNextAvailableTimes(selectedDay: number) {
    const availableSlots = this.buildAvailableSlotsForDay(selectedDay);
    const hasAMTimes = availableSlots.am.length > 0;
    const hasPMTimes = availableSlots.pm.length > 0;
    if (hasAMTimes) {
      this.currentDayPart = 'AM';
    } else if (hasPMTimes) {
      this.currentDayPart = 'PM';
    }
  }

  autoSelectSelectedDateTime(selectedTime: IDay) {
    this.buildAvailableSlotsForDay(this.selectedTime.day_week);
    this.__selectedDayPart = selectedTime.am ? 'AM' : 'PM';
    this.selectedDay = selectedTime.day_week;
  }

  set currentDayPart(day_part: t_day_part) {
    this.__selectedDayPart = day_part;
  }

  get currentDayPart() {
    return this.__selectedDayPart;
  }

  isPreviousWeekEnabled(): boolean {
    const diff: number = this.next_week_first_day.startOf('week').diff(moment().startOf('week'), 'week');
    return diff >= 2;
  }
  resetAvailableDayTimes() {
    this.availableTimeSlots = {
      am: [],
      pm: [],
      all: []
    };
  }
  nextWeek(): void {
    this.selectedTime = {} as IDay;
    this.selected_Time = {} as IDay;
    this.onNextWeekSelection().then((times: moment.Moment[]) => {
      this.data = this.constructDates(times);
      const start_of_week: moment.Moment =
        _4.size(this.data) === 0 ? this.next_week_first_day : moment(this.data[0].date).startOf('week');
      this.week = this.getWeek(start_of_week);
      this.resetAvailableDayTimes();
      this.autoSelectFirstAvailableDateInCurrentWeek();
      this.isPreviousWeekEnabled();
    });
  }

  previousWeek(): void {
    this.selectedTime = {} as IDay;
    this.selected_Time = {} as IDay;
    this.onPreviousWeekSelection().then((times: moment.Moment[]) => {
      this.data = this.constructDates(times);
      const start_of_week: moment.Moment =
        _4.size(this.data) === 0 ? this.previous_week_first_day : moment(this.data[0].date).startOf('week');
      this.week = this.getWeek(start_of_week);
      this.resetAvailableDayTimes();
      this.autoSelectFirstAvailableDateInCurrentWeek();
      this.isPreviousWeekEnabled();
    });
  }

  selectTime(time: IDay): void {
    if (time.selected) {
      return;
    }
    time.selected = true;
    _4.forEach(_4.filter(this.data, { selected: true }), (day: IDay) => {
      day.selected = day !== time ? false : day.selected;
    });
    this.selected_Time = _4.first(_4.filter(this.data, { selected: true }));
    this.selected_Date = time.day_week;
    this.onSelect({ item: this.selected_Time });
  }

  amTimeSelected(day: number): boolean {
    if (this.selectedTime && this.selectedTime.am) {
      return day === this.selectedTime.day_week;
    } else if (this.selected_Time && this.selected_Time.am) {
      return day === this.selected_Date;
    } else {
      return false;
    }
  }
  pmTimeSelected(day: number): boolean {
    if (this.selectedTime && !this.selectedTime.am) {
      return day === this.selectedTime.day_week;
    } else if (this.selected_Time && !this.selected_Time.am) {
      return day === this.selected_Date;
    } else {
      return false;
    }
  }
}

angular
  .module('ui-ng-week-times', [])
  .controller('ngPhxWeekTimesController', PhxWeekTimesController)
  .component('phxWeekTimes', {
    templateUrl: 'ui-ng-week-times/src/templates/phx-week-times.html',
    controller: 'ngPhxWeekTimesController',
    controllerAs: '$ctrl',
    bindings: {
      date: '<',
      timings: '<',
      onSelect: '&',
      selectedTime: '<',
      onNextWeekSelection: '&',
      onPreviousWeekSelection: '&'
    }
  });

export const b = 'ui-ng-week-times';



// WEBPACK FOOTER //
// ./app/vendor/ui-ng-week-times/src/main.ts